<section class="windows-sql-hero">
  <div class="container">
    <div class="row align-items-center">
      <div class="col-lg-6">
        <div class="hero-content">
          <span class="hero-badge">
            <i class="bx bxl-windows"></i>
            Windows SQL Server Cloud
          </span>
          <h1 class="hero-title">
            Enterprise-Grade Windows SQL Cloud
          </h1>
          <p class="hero-description">
            Deploy fully managed, high-performance SQL Server instances in minutes, engineered for mission-critical workloads with 99.99% SLA availability.
          </p>

          <div class="hero-actions">
            <a class="btn-primary-gradient me-3" routerLink="/contact">
              <i class="bx bx-phone"></i>
              Talk to Sales
            </a>
            <a class="btn-outline-light" routerLink="/documentation">
              <i class="bx bx-file"></i>
              View Documentation
            </a>
          </div>

          <div class="hero-features">
            <div class="feature-item">
              <i class="bx bx-check-shield"></i>
              <span>SOC2 compliant platform</span>
            </div>
            <div class="feature-item">
              <i class="bx bx-time-five"></i>
              <span>99.99% uptime SLA</span>
            </div>
            <div class="feature-item">
              <i class="bx bx-support"></i>
              <span>24/7 SQL experts</span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6">
        <div class="hero-image">
          <div class="image-decoration">
            <div class="sql-dashboard-card">
              <div class="sql-dashboard-header">
                <div class="sql-dashboard-title">
                  <i class="bx bxl-microsoft"></i>
                  <div>
                    <h3>SQL Server 2022</h3>
                    <span>Enterprise Edition</span>
                  </div>
                </div>
                <span class="sql-status-pill">Running</span>
              </div>
              <div class="sql-dashboard-body">
                <div class="sql-stat">
                  <span class="label">CPU Usage</span>
                  <span class="value">45%</span>
                </div>
                <div class="sql-stat">
                  <span class="label">Memory</span>
                  <span class="value">12.4 GB</span>
                </div>
                <div class="sql-stat">
                  <span class="label">IOPS</span>
                  <span class="value">12,500</span>
                </div>
                <div class="sql-stat">
                  <span class="label">Connections</span>
                  <span class="value">842</span>
                </div>
              </div>
            </div>

            <div class="floating-card card-1">
              <i class="bx bx-check-circle"></i>
              <span>Automated Backups</span>
            </div>
            <div class="floating-card card-2">
              <i class="bx bx-lock-alt"></i>
              <span>Encrypted at Rest</span>
            </div>
            <div class="floating-card card-3">
              <i class="bx bx-trending-up"></i>
              <span>Always On Monitoring</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="hero-background-elements">
    <div class="bg-element element-1"></div>
    <div class="bg-element element-2"></div>
    <div class="bg-element element-3"></div>
  </div>
</section>

<section class="windows-sql-features ptb-100">
  <div class="container">
    <div class="section-title text-center">
      <span class="section-badge">Why Choose Us</span>
      <h2>Optimized for Performance &amp; Reliability</h2>
      <p>
        Our Windows SQL Cloud infrastructure is built on enterprise-grade hardware and optimized specifically for SQL Server workloads.
      </p>
    </div>

    <div class="row">
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon">
            <i class="bx bx-rocket"></i>
          </div>
          <h3>NVMe Acceleration</h3>
          <p>
            Lightning-fast storage performance with 100% NVMe drives, ensuring low latency for high-transaction databases.
          </p>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon purple">
            <i class="bx bx-shield-quarter"></i>
          </div>
          <h3>Advanced Security</h3>
          <p>
            Transparent Data Encryption, always-on SSL, and isolated network environments keep your data secure.
          </p>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon cyan">
            <i class="bx bx-refresh"></i>
          </div>
          <h3>Auto-Healing</h3>
          <p>
            Self-healing architecture automatically detects and recovers from hardware failures without manual intervention.
          </p>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon orange">
            <i class="bx bx-slider-alt"></i>
          </div>
          <h3>Flexible Scaling</h3>
          <p>
            Scale compute and storage independently. Resize instances in minutes to handle peak workloads efficiently.
          </p>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon green">
            <i class="bx bx-world"></i>
          </div>
          <h3>Global Availability</h3>
          <p>
            Deploy close to your users in one of our global data centers to minimize latency and meet compliance needs.
          </p>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 mb-4">
        <div class="feature-card">
          <div class="feature-icon red">
            <i class="bx bx-support"></i>
          </div>
          <h3>Managed Services</h3>
          <p>
            Let our certified DBAs handle patching, backups, and performance tuning so you can focus on your applications.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>


<section class="windows-sql-specs ptb-100">
  <div class="container">
    <div class="section-title text-center">
      <span class="section-badge">Technical Specifications</span>
      <h2>Enterprise-Ready Architecture</h2>
      <p>Review the core software and hardware specifications behind our managed Windows SQL Cloud platform.</p>
    </div>

    <div class="row">
      <div class="col-lg-6 mb-4">
        <div class="spec-card">
          <h3>
            <i class="bx bxl-windows"></i>
            Software
          </h3>
          <ul>
            <li>
              <span class="label">OS Versions</span>
              <span class="value">Windows Server 2019, 2022</span>
            </li>
            <li>
              <span class="label">SQL Versions</span>
              <span class="value">SQL Server 2017, 2019, 2022</span>
            </li>
            <li>
              <span class="label">Access</span>
              <span class="value">Full RDP &amp; SSMS access</span>
            </li>
            <li>
              <span class="label">Updates</span>
              <span class="value">Automated patch management</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="col-lg-6 mb-4">
        <div class="spec-card">
          <h3>
            <i class="bx bx-chip"></i>
            Hardware
          </h3>
          <ul>
            <li>
              <span class="label">Processors</span>
              <span class="value">Intel Xeon Scalable / AMD EPYC</span>
            </li>
            <li>
              <span class="label">Storage</span>
              <span class="value">Enterprise NVMe SSD</span>
            </li>
            <li>
              <span class="label">Network</span>
              <span class="value">10 Gbps uplinks</span>
            </li>
            <li>
              <span class="label">Backup</span>
              <span class="value">Daily snapshots included</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="windows-sql-faq ptb-100">
  <div class="container">
    <div class="section-title text-center">
      <span class="section-badge">FAQ</span>
      <h2>Frequently Asked Questions</h2>
      <p>Have questions about Windows SQL Cloud? Find quick answers below.</p>
    </div>

    <div class="faq-accordion">
      <div
        class="accordion-item"
        *ngFor="let faq of faqs; let i = index"
        [class.active]="faq.isOpen"
      >
        <button type="button" class="accordion-header" (click)="toggleFaq(i)">
          <h3>{{ faq.question }}</h3>
          <div class="accordion-icon">
            <i class="bx" [ngClass]="faq.isOpen ? 'bx-minus' : 'bx-plus'"></i>
          </div>
        </button>
        <div class="accordion-content" *ngIf="faq.isOpen">
          <p>{{ faq.answer }}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<section class="windows-sql-cta ptb-100">
  <div class="container">
    <div class="row align-items-center">
      <div class="col-lg-8">
        <h2>Ready to Optimize Your SQL Server Workloads?</h2>
        <p>Join enterprises that trust our secure, fully managed Windows SQL Cloud for their most critical databases.</p>
      </div>
      <div class="col-lg-4 text-lg-end">
        <a class="btn-primary-gradient me-3 mb-3 mb-lg-0" routerLink="/contact">
          <i class="bx bx-phone"></i>
          Contact Sales
        </a>
        <a class="btn-outline-light" routerLink="/documentation">
          <i class="bx bx-file"></i>
          View Guides
        </a>
      </div>
    </div>
  </div>
</section>
